import React from 'react'
import { Icon, Tooltip } from 'antd'
import './StyleCard.scss'

const StyleCard = ({
  style,
  isSelected,
  onSelect,
  isCreateNew = false,
  onClick,
  onPreview, // New prop for opening preview modal
  onDelete,
  isSuperAdmin,
  deletingStyleId,
  className
}) => {
  const videoRef = React.useRef(null)
  const [isHovering, setIsHovering] = React.useState(false)
  const [isVideoReady, setIsVideoReady] = React.useState(false)

  const isDeleting = deletingStyleId === style?.key
  const isAnyDeleting = !!deletingStyleId && !isDeleting

  if (isCreateNew) {
    return (
      <div className="style-card create-new-card" onClick={onClick}>
        <div className="card-content centered">
          <div className="icon-wrapper">
            <Icon type="plus" />
          </div>
          <div className="text-group">
            <h4>Create New Style</h4>
            <p>Upload videos to generate a custom style</p>
          </div>
        </div>
      </div>
    )
  }

  // Determine background media
  const hasThumbnailImage = !!style?.thumbnailImage
  const hasThumbnailVideo = !!style?.thumbnailVideo

  // Handlers for hover video playback
  const handleMouseEnter = () => {
    setIsHovering(true)
    // Optimization: If we have a thumbnail image, the video is only mounted on hover (see render below).
    // In that case, it has autoPlay, so we don't need to play it manually here.
    // We only manually play if it's a "Video Only" card (no image) where the video is always in the DOM.
    if (!hasThumbnailImage && videoRef.current && hasThumbnailVideo) {
      videoRef.current.play().catch(e => {
        // Autoplay might be blocked, silent fail
        console.warn('Video play blocked', e)
      })
    }
  }

  const handleVideoCanPlay = () => {
    // Only handle playback here, not visibility. Visibility is handled by onLoadedData
    // which guarantees a frame is rendered, preventing black flashes.
    if (isHovering && videoRef.current) {
      videoRef.current.play().catch(e => console.warn('Video play blocked', e))
    }
  }

  const handleMouseLeave = () => {
    setIsHovering(false)
    setIsVideoReady(false) // Reset readiness on leave so it re-buffers/re-loads if needed next time
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }

  const handlePreviewClick = e => {
    e.stopPropagation()
    if (onPreview) {
      // Get reference videos from raw data if available (for custom styles)
      const referenceVideos =
        style.raw && style.raw.style_video && style.raw.style_video.length > 0
          ? style.raw.style_video
          : []

      // Pass style info including type, thumbnail video, and reference videos
      onPreview({
        title: style.title,
        isCustom: !style.isSystem,
        thumbnailVideo: style.thumbnailVideo,
        referenceVideos: referenceVideos
      })
    }
  }

  return (
    <div
      className={`style-card ${isSelected ? 'selected' : ''} ${
        isDeleting ? 'deleting' : ''
      } ${className || ''}`}
      onClick={() => !isDeleting && onSelect(style)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="style-card-inner">
        {/* Background Layer */}
        <div className="card-media-layer">
          {hasThumbnailImage ? (
            <div className="card-background-image">
              <img src={style.thumbnailImage} alt={style.title} />
              {/* Show video on hover ONLY if it is ready to play */}
              {hasThumbnailVideo && isHovering && (
                <video
                  ref={videoRef}
                  src={style.thumbnailVideo}
                  muted
                  loop
                  autoPlay
                  playsInline
                  onCanPlay={handleVideoCanPlay}
                  onLoadedData={() => setIsVideoReady(true)}
                  className={`hover-video ${isVideoReady ? 'visible' : ''}`}
                />
              )}
            </div>
          ) : hasThumbnailVideo ? (
            // No image, only video - act as background
            <div className="card-background-video">
              <video
                ref={videoRef}
                src={style.thumbnailVideo}
                muted
                loop
                playsInline
                preload="auto"
                onCanPlay={handleVideoCanPlay}
                onLoadedMetadata={e => {
                  // Seek to 1s to ensure we show a meaningful frame
                  if (videoRef.current) {
                    videoRef.current.currentTime = 1
                  }
                }}
                onLoadedData={() => setIsVideoReady(true)}
                className="main-video"
              />
            </div>
          ) : (
            <div className={`card-background-gradient ${style.key}`} />
          )}
          <div className="image-overlay" />
        </div>
        {/* Content Layer */}
        <div
          className={`card-content ${
            hasThumbnailImage || hasThumbnailVideo ? 'has-media' : ''
          }`}
        >
          {style.key === 'swivt_education' ||
          style.title === 'Swivt Education' ? (
            <div className="brand-logo">
              {/* Placeholder for logo if needed */}
            </div>
          ) : null}

          <div className="card-content-main">
            <div className="card-info">
              <Tooltip
                title={style.title}
                placement="top"
                mouseEnterDelay={0.5}
              >
                <h4>{style.title}</h4>
              </Tooltip>
              <div className="description-row">
                <Tooltip
                  title={style.description}
                  placement="top"
                  mouseEnterDelay={0.5}
                >
                  <p>{style.description}</p>
                </Tooltip>
              </div>
            </div>

            {/* Preview Button - Reveals from bottom on hover */}
            {hasThumbnailVideo && (
              <Tooltip title="View Video" placement="top">
                <div
                  className={`preview-action-btn ${
                    isHovering ? 'visible' : ''
                  }`}
                  onClick={handlePreviewClick}
                >
                  <Icon type="caret-right" theme="filled" />
                </div>
              </Tooltip>
            )}
          </div>
        </div>
        {/* Delete Action - For custom styles OR system styles if superadmin */}
        {(!style.isSystem || (style.isSystem && isSuperAdmin)) &&
          !style.isDefault &&
          !isDeleting && (
            <div
              className={`delete-action-btn ${isAnyDeleting ? 'disabled' : ''}`}
              onClick={e => {
                e.stopPropagation()
                if (!isAnyDeleting && onDelete) {
                  onDelete(style)
                }
              }}
            >
              <div className="delete-icon-wrapper">
                <Icon type="delete" />
              </div>
            </div>
          )}
        {isDeleting && (
          <div className="deleting-overlay">
            <Icon type="loading" />
            <span>Deleting...</span>
          </div>
        )}
        {isSelected && (
          <div className="selected-indicator">
            <Icon type="check-circle" theme="filled" />
            <span>Selected</span>
          </div>
        )}
        <div className="custom-badge">
          {' '}
          {style.isDefault ? 'Default' : style.isSystem ? 'System' : 'Custom'}
        </div>
      </div>
    </div>
  )
}

export default StyleCard
